import { MockServiceStore } from "./MockServiceStore";
import { mockStructureCastCastingDays } from "./structurecast-casting-days.mock";

/**
 * ASTM C39 cylinder break result. Three are typically cast per batch ticket
 * and broken at 7, 14, and 28 days.
 */
export interface CylinderTestResult {
  cylinderId: string;
  ageDays: 7 | 14 | 28;
  breakDate: string;
  specifiedPsi: number;
  actualPsi: number;
  pass: boolean;
  technician: string;
}

/**
 * StructureCast - Batch Ticket (concrete delivery record for a casting day)
 */
export interface StructureCastBatchTicket {
  id: string;
  castingDayId: string;
  ticketNumber: string;
  date: string;
  concreteSupplier: string;
  mixDesign: string;
  cubicYardsDelivered: number;
  slumpTest: {
    specified: number; // inches
    measured: number;
    pass: boolean;
  };
  airContent: {
    specified: number; // percent
    measured: number;
    pass: boolean;
  };
  temperature: number; // degrees F
  cylinderTests: CylinderTestResult[];
  notes: string;
  createdAt: string;
  updatedAt: string;
}

const SPECIFIED_PSI = 5000;
const CYLINDER_AGES: Array<7 | 14 | 28> = [7, 14, 28];

/**
 * Realistic compressive-strength curve relative to design strength.
 * 7-day  ≈ 65-75% of design
 * 14-day ≈ 85-95% of design
 * 28-day ≈ 100-115% of design (rare under-runs ~95%)
 */
const psiByAge = (age: 7 | 14 | 28, seed: number): number => {
  const noise = (seed % 11) / 100; // 0–0.10
  const sign = seed % 2 === 0 ? 1 : -1;
  let pct: number;
  if (age === 7) pct = 0.70 + sign * noise;        // 0.60–0.80
  else if (age === 14) pct = 0.90 + sign * noise;  // 0.80–1.00
  else pct = 1.08 + sign * noise;                  // 0.98–1.18
  return Math.round(SPECIFIED_PSI * pct);
};

const addDays = (isoDate: string, days: number): string => {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

/**
 * Generate batch tickets (+ cylinder break results) for completed casting days.
 * Mid-State Concrete Products — Santa Maria, CA (Central Coast).
 */
const generateBatchTickets = (): StructureCastBatchTicket[] => {
  const tickets: StructureCastBatchTicket[] = [];
  let ticketNum = 88401; // CalPortland Santa Maria ticket numbering

  const completedDays = mockStructureCastCastingDays.filter(
    (d) => d.status === "completed"
  );

  for (const day of completedDays) {
    // 1 batch ticket per casting day (sometimes 2 for larger pours)
    const numTickets = day.totalCubicYards > 4.0 ? 2 : 1;

    for (let t = 0; t < numTickets; t++) {
      const cyDelivered = numTickets === 1
        ? day.totalCubicYards
        : +(day.totalCubicYards / 2).toFixed(2);

      const slumpMeasured = 3.5 + Math.random() * 2; // 3.5–5.5" typical
      const airMeasured = 5 + Math.random() * 2;    // 5–7% typical

      const castDate = day.actualCastDate || day.scheduledDate;
      const cylinderTests: CylinderTestResult[] = CYLINDER_AGES.map((age, idx) => {
        const seed = ticketNum + age + idx;
        const actualPsi = psiByAge(age, seed);
        return {
          cylinderId: `CYL-${ticketNum}-${String.fromCharCode(65 + idx)}`,
          ageDays: age,
          breakDate: addDays(castDate, age),
          specifiedPsi: SPECIFIED_PSI,
          actualPsi,
          // ASTM C39 pass criterion: 28-day must meet/exceed specified;
          // 7/14-day are progress indicators (informational only).
          pass: age === 28 ? actualPsi >= SPECIFIED_PSI : actualPsi >= SPECIFIED_PSI * (age === 7 ? 0.65 : 0.85),
          technician: "Mid-State QC",
        };
      });

      tickets.push({
        id: `sc-bt-${ticketNum}`,
        castingDayId: day.id,
        ticketNumber: String(ticketNum),
        date: castDate,
        concreteSupplier: "CalPortland — Santa Maria",
        mixDesign: "5,000 PSI",
        cubicYardsDelivered: cyDelivered,
        slumpTest: {
          specified: 4,
          measured: +slumpMeasured.toFixed(1),
          pass: slumpMeasured >= 3 && slumpMeasured <= 6,
        },
        airContent: {
          specified: 6,
          measured: +airMeasured.toFixed(1),
          pass: airMeasured >= 4 && airMeasured <= 8,
        },
        temperature: 50 + Math.round(Math.random() * 18), // 50–68°F (Central Coast winter)
        cylinderTests,
        notes: t === 0 ? "" : "Second load for same pour",
        createdAt: `${castDate}T07:00:00.000Z`,
        updatedAt: `${castDate}T08:00:00.000Z`,
      });

      ticketNum++;
    }
  }

  return tickets;
};

export const mockStructureCastBatchTickets: StructureCastBatchTicket[] = generateBatchTickets();

export const createMockStructureCastBatchTicketStore = () =>
  new MockServiceStore<StructureCastBatchTicket>({
    mockData: mockStructureCastBatchTickets,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-batch-tickets",
  });

export default mockStructureCastBatchTickets;
